import { Card } from '@ovComponents/2-component/card/card';
import {
  DataTable,
  type DataTableTanstackTable,
  type Row,
  type RowsPerPageOption,
} from '@ovComponents/2-component/dataTable';
import {
  dataTableMetaToCellProps,
  getDataTableColumnMeta,
} from '@ovComponents/2-component/dataTable/dataTableMetaProps';
import { DataTableTablePaginationFooter } from '@ovComponents/2-component/dataTable/dataTablePaginationFooter';
import { DataTableSkeletonTableBody } from '@ovComponents/2-component/dataTable/dataTableSkeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { flexRender } from '@tanstack/react-table';
import type { Theme } from 'hephaestus/core';
import { Box, Button, Typography } from 'hephaestus/web';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import type { ActivitiesGridActivity } from '../utils/activities.queries';

export type ActivitiesTableCardProps = {
  theme: Theme;
  table: DataTableTanstackTable<ActivitiesGridActivity>;
  pageSize: RowsPerPageOption;
  tableLoading: boolean;
  pageCount: number;
  page: number;
  onPageChange: (page: number) => void;
  totalRowCount: number;
  onPerPageChange: (perPage: number) => void;
  onRowClick: (row: ActivitiesGridActivity) => void;
  expandedActivityIds: ReadonlySet<string>;
  selectedCount: number;
  showBulkBar: boolean;
  onSelectAllOnPage: () => void;
  onClearSelection: () => void;
};

export const ActivitiesTableCard = ({
  theme,
  table,
  pageSize,
  tableLoading,
  pageCount,
  page,
  onPageChange,
  totalRowCount,
  onPerPageChange,
  onRowClick,
  expandedActivityIds,
  selectedCount,
  showBulkBar,
  onSelectAllOnPage,
  onClearSelection,
}: ActivitiesTableCardProps) => {
  const { t } = useTranslation(['reconciliation']);
  const leafColumnCount = table.getAllLeafColumns().length;

  const showSelectAllOnPage = table
    .getRowModel()
    .rows.some(
      (row: Row<ActivitiesGridActivity>) =>
        row.getCanSelect() && !row.getIsSelected(),
    );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      {showBulkBar && selectedCount > 0 ? (
        <Card
          variant='outlined'
          sx={{
            backgroundColor: theme.color.primaryVariant,
            borderColor: theme.color.primaryVariantPressed,
            boxShadow: 'none',
          }}
        >
          <Box
            sx={{
              py: 1,
              pr: 1,
              pl: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant='bodyMedium'>
              {t('activitiesV3.bulk.selectedCount', { count: selectedCount })}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {showSelectAllOnPage ? (
                <Button size='small' variant='text' onClick={onSelectAllOnPage}>
                  {t('activitiesV3.bulk.selectAllOnPage')}
                </Button>
              ) : null}
              <Button size='small' variant='text' onClick={onClearSelection}>
                {t('activitiesV3.bulk.clearSelection')}
              </Button>
              <Button
                size='small'
                variant='contained'
                color='primary'
                disabled
                data-testid='activities-bulk-retry'
              >
                {t('activitiesV3.bulk.retry')}
              </Button>
            </Box>
          </Box>
        </Card>
      ) : null}

      <Box sx={{ width: '100%', minWidth: 0 }}>
        <Box
          data-testid='data-table-horizontal-scroll'
          sx={{
            width: '100%',
            minWidth: 0,
            overflowX: 'auto',
            overflowY: 'visible',
          }}
        >
          <Table data-testid='activities-table' sx={{ overflow: 'visible' }}>
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const meta = getDataTableColumnMeta(header.column);
                    return (
                      <TableHeadCell
                        key={header.id}
                        colSpan={header.colSpan}
                        {...dataTableMetaToCellProps(meta)}
                        headLabelVariant={meta?.headerLabelVariant}
                        headLabelWeight={meta?.headerLabelWeight}
                        headLabelSx={meta?.headerLabelSx}
                        textColor={meta?.headerTextColor}
                        headerBare={meta?.headerBare}
                        isSortable={false}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHeadCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableHead>
            {tableLoading ? (
              <DataTableSkeletonTableBody
                rowCount={Math.max(1, pageSize)}
                columns={table.getVisibleLeafColumns()}
                testId='activities-table'
              />
            ) : (
              <TableBody>
                {table.getRowModel().rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={leafColumnCount}>
                      <Typography variant='bodyMedium' color='textSecondary'>
                        {t('activitiesV3.table.empty')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  table
                    .getRowModel()
                    .rows.map((row: Row<ActivitiesGridActivity>) => {
                      const activity = row.original;
                      const expanded = expandedActivityIds.has(activity.id);
                      return (
                        <Fragment key={row.id}>
                          <TableRow
                            hover
                            onClick={() => onRowClick(activity)}
                            sx={{ cursor: 'pointer' }}
                            data-testid={`activities-row-${activity.id}`}
                          >
                            {row.getVisibleCells().map((cell) => {
                              const meta = getDataTableColumnMeta(cell.column);
                              return (
                                <TableCell
                                  key={cell.id}
                                  {...dataTableMetaToCellProps(meta)}
                                >
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext(),
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                          {expanded ? (
                            <TableRow
                              data-testid={`activities-detail-${activity.id}`}
                            >
                              <TableCell colSpan={leafColumnCount}>
                                <Box
                                  sx={{
                                    py: 2,
                                    px: 1,
                                    borderRadius: '8px',
                                    bgcolor: String(theme.color.surface),
                                    border: '1px dashed',
                                    borderColor: 'divider',
                                  }}
                                >
                                  <Typography
                                    variant='bodyMedium'
                                    color='textSecondary'
                                  >
                                    {t('activitiesV3.table.detailPlaceholder')}
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ) : null}
                        </Fragment>
                      );
                    })
                )}
              </TableBody>
            )}
          </Table>
        </Box>
        <DataTableTablePaginationFooter
          table={table}
          options={{
            mode: 'controlled',
            page,
            pageCount,
            totalRowCount,
            perPage: pageSize,
            onPageChange,
            onPerPageChange,
            ovVariant: 'tableFooter',
          }}
          loading={tableLoading}
        />
      </Box>
    </Box>
  );
};

/** Kept for callers that prefer the shared DataTable wrapper without expand rows. */
export const ActivitiesTableDataTable = DataTable;
